<template>

    <div class="ui-date-picker" :class="{ 'mode-period': period, 'mode-single': !period }" v-spinner="loading">

        <div class="navigator">
            <a href="#" @click.prevent="navigate(-1)" class="previous" aria-label="previous"><i class="bx bx-chevron-left"></i></a>
            <a href="#" class="title" @click.prevent="showYearPicker" aria-label="Show dropdown">{{ title }} <i class="bx bx-chevron-down"></i></a>
            <a href="#" @click.prevent="navigate(1)" class="next" aria-label="next"><i class="bx bx-chevron-right"></i></a>
        </div>

        <div class="grid">
            <div class="header">
                <div class="day" v-for="name of dayNames">{{ name }}</div>
            </div>

            <div class="week" v-for="week of weeks">

                <div class="day" v-for="day of week.days" :class="getDayClasses(day)">

                    <a href="#" class="day-item" @click.prevent="selectDate(day)"
                       @mouseenter="onDayEnter(day)" @mouseleave="onDayLeave(day)">
                        <span>{{ day.day }}</span>
                    </a>

                </div>
            </div>
        </div>

        <div v-if="timeEnabled" class="time">
            <ui-input
                prefix="Tijd" cleave="time"
                :modelValue="timeValue" placeholder="00:00"
                @update:modelValue="onTimeInput"></ui-input>
        </div>
    </div>

</template>

<script lang="ts">

import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';

import { DateTime, Interval } from 'luxon';
import { chunk, first, map, range } from 'lodash';

import type { ComponentProps } from '@wittignl/js-core';
import { createComponentMixin, ucFirstFilter } from '@wittignl/js-core';

import popupMixin from '../../mixin/popup';
import spinnerDirective from '../../directive/spinner';

import Input from './Input.vue';
import OptionPickerPopup from './../popup/OptionPickerPopup.vue';

const MIN_YEAR_OFFSET = 100;

@Component({
    name: 'ui-date-picker',
    components: {
        'ui-input': Input
    },
    emits: ['update:modelValue', 'navigate'],
    mixins: [popupMixin],
    directives: [spinnerDirective]
})
export default class DatePicker extends Vue {

    @Prop({ type: [String, Array] })
    modelValue: string | string[];

    @Prop({ type: Boolean })
    time: boolean;

    @Prop({ type: Boolean })
    period: boolean;

    @Prop({ type: String })
    minDate: string;

    @Prop({ type: String })
    maxDate: string;

    @Prop({ type: Number })
    minPeriod: number;

    @Prop({ type: Number })
    maxPeriod: number;

    @Prop({ type: Object })
    dateClasses: Record<string, string>;

    @Prop({ type: Boolean })
    loading: boolean;

    @Prop({ type: Function })
    validateDate: Function;

    selectedDate: string = null;
    selectedPeriod: string[] = null;

    pendingPeriod: string[] = null;

    timeValue: string = null;
    visibleDate: string = null;

    created() {

        this.updateFromValue(this.modelValue);
        this.updateVisibleDate();
    }

    mounted(){

        this.emitNavigate();
    }

    @Watch('modelValue')
    onModelValueUpdate(value) {

        this.updateFromValue(value);

        if(value){
            this.updateVisibleDate();
        }
    }

    updateVisibleDate() {

        let visible: DateTime;

        if(this.period){
            visible = this.selectedPeriodLuxon?.length ? this.selectedPeriodLuxon[0] : null;
        }
        else {
            visible = this.selectedDateLuxon;
        }

        if(!visible && this.minDate){
            visible = DateTime.fromISO(this.minDate);
        }

        if(!visible){
            visible = DateTime.now();
        }

        this.visibleDate = visible.toISODate();
    }

    navigate(direction) {

        this.visibleDate = DateTime.fromISO(this.visibleDate).plus({ months: direction }).toISODate();

        this.emitNavigate();
    }

    emitNavigate(){

        this.$emit('navigate', {
            startDate: this.visiblePeriod.startDate.toISO(),
            endDate: this.visiblePeriod.endDate.toISO()
        });
    }

    onDayEnter(day){

        if(this.period && this.selectedPeriodLuxon?.length == 1 && !day.disabled && this.isDateRangeSelectionValid(DateTime.fromISO(day.date))){
            this.pendingPeriod = [this.selectedPeriod[0], day.date];
        }
        else {
            this.pendingPeriod = null;
        }
    }

    onDayLeave(day){

        this.pendingPeriod = null;
    }

    selectDate(day) {

        if(day.disabled){
            return;
        }

        let luxonDate = DateTime.fromISO(day.date);

        if(this.period){

            if(this.isDateRangeSelectionValid(luxonDate)){
                this.updatePeriodValue(luxonDate);
            }
        }
        else {

            if(this.timeEnabled && this.timeParts){
                luxonDate = luxonDate.set(this.timeParts);
            }

            this.updateSingleValue(luxonDate);
        }
    }

    updateSingleValue(date: DateTime) {

        const formattedDate = date ? (this.time ? date.toISO() : date.toISODate()) : null;

        this.selectedDate = formattedDate;
        this.selectedPeriod = null;

        this.$emit('update:modelValue', formattedDate);
    }

    updatePeriodValue(date: DateTime) {

        const formattedDate = date.toISODate();

        this.selectedDate = null;
        this.pendingPeriod = null;

        if(!this.selectedPeriod?.length || this.selectedPeriod.length >= 2){

            this.selectedPeriod = [formattedDate];
        }
        else {

            this.selectedPeriod.push(formattedDate);
            this.$emit('update:modelValue', [...this.selectedPeriod]);
        }
    }

    updateFromValue(value: string | string[]) {

        if(this.period){

            this.selectedPeriod = value as string[] || null;
            this.selectedDate = null;
        }
        else {

            this.selectedDate = value as string || null;
            this.selectedPeriod = null;

            if(this.selectedDateLuxon && this.timeEnabled){
                this.timeValue = this.selectedDateLuxon.toFormat('HH:mm');
            }
        }
    }

    onTimeInput(value: string) {

        this.timeValue = value;

        if(this.selectedDateLuxon && this.timeEnabled && this.timeParts){

            const luxonDate = this.selectedDateLuxon.set(this.timeParts);

            this.updateSingleValue(luxonDate);
        }
    }

    getDayClasses(day) {

        const dateLuxon = DateTime.fromISO(day.date);

        let classes: string[] = [];

        if(day.disabled){
            classes.push('disabled');
        }

        if(day.ghost){
            classes.push('ghost');
        }

        if(this.dateClasses && this.dateClasses[day.date]){
            classes.push(this.dateClasses[day.date]);
        }

        if(this.period){

            if(this.selectedPeriodInterval){

                if(this.selectedPeriodInterval.start.hasSame(dateLuxon, 'day')){
                    classes.push('range-selected', 'range-selected-start');
                }

                if(this.selectedPeriodInterval.end.hasSame(dateLuxon, 'day')){
                    classes.push('range-selected', 'range-selected-end');
                }

                if(this.selectedPeriodInterval.contains(dateLuxon)){
                    classes.push('range-selected');
                }
            }
            else if(this.selectedPeriodLuxon?.length == 1 && this.selectedPeriodLuxon[0].hasSame(dateLuxon, 'day')) {
                classes.push('range-selected', 'range-selected-start');
            }

            if(!this.isDateRangeSelectionValid(dateLuxon)){
                classes.push('disabled');
            }

            if(this.pendingPeriod && this.pendingPeriod[0] != day.date) {

                if(day.date == this.pendingPeriod[1]){
                    classes.push('range-hover', 'range-hover-end');
                }
                else if(day.date > this.pendingPeriod[0] && day.date < this.pendingPeriod[1]){
                    classes.push('range-hover');
                }
            }
        }
        else if(this.selectedDateLuxon && this.selectedDateLuxon.hasSame(dateLuxon, 'day')){
            classes.push('selected');
        }

        return classes;
    }

    isDateRangeSelectionValid(date: DateTime): boolean {

        if(this.selectedPeriodLuxon?.length !== 1){
            return true;
        }

        const startDate = this.selectedPeriodLuxon[0];

        if(date < startDate){
            return false;
        }

        if(this.maxPeriod || this.minPeriod){

            const duration = date.diff(startDate, 'days').days + 1;

            if(this.maxPeriod && duration > this.maxPeriod){
                return false;
            }

            if(this.minPeriod && duration < this.minPeriod){
                return false;
            }
        }

        return true;
    }

    async showYearPicker(e){

        const refYear = new Date().getFullYear();

        let fromYear = refYear - MIN_YEAR_OFFSET;

        if(this.minDate){
            fromYear = DateTime.fromISO(this.minDate).year;
        }

        let toYear = refYear + 1;

        if(this.maxDate){
            toYear = DateTime.fromISO(this.maxDate).year;
        }

        let yearOptions = range(fromYear, toYear + 1).reverse().map(year => ({
            label: year.toString(),
            value: year
        }));

        const { result } = await this.$popup(OptionPickerPopup, {
            anchor: e.target,
            placement: 'bottom'
        }, {
            options: yearOptions,
            width: 75,
            centered: true,
            modelValue: this.visibleDateLuxon.year
        });

        if(result){
            
            this.visibleDate = this.visibleDateLuxon.set({ year: result.value }).toISODate();
            this.emitNavigate();
        }
    }

    get title() {

        return ucFirstFilter(DateTime.fromISO(this.visibleDate).toFormat('LLLL y'));
    }

    get weeks() {

        let days = [];

        const visibleDateLuxon = DateTime.fromISO(this.visibleDate);
        const startDate = this.visiblePeriod.startDate;
        const endDate = this.visiblePeriod.endDate;

        let cursor = startDate;

        do {

            const date = cursor.toISODate();
            let disabled = false;

            if(this.minDate && date < this.minDate){
                disabled = true;
            }

            if(this.maxDate && date > this.maxDate){
                disabled = true;
            }

            if(this.validateDate && !this.validateDate(date, this.selectedPeriod)){
                disabled = true;
            }

            days.push({
                day: cursor.toFormat('d'),
                week: cursor.toFormat('W'),
                date,
                ghost: !cursor.hasSame(visibleDateLuxon, 'month'),
                disabled
            });

            cursor = cursor.plus({ days: 1 });
        }
        while(cursor < endDate);

        return map(chunk(days, 7), chunk => {

            return {
                week: first(chunk).week,
                days: chunk
            };
        });
    }

    get visiblePeriod(): { startDate: DateTime, endDate: DateTime } {

        const visibleDateLuxon = DateTime.fromISO(this.visibleDate);
        const startDate = visibleDateLuxon.startOf('month').startOf('week');
        const endDate = visibleDateLuxon.endOf('month').endOf('week');

        return { startDate, endDate };
    }

    get dayNames() {

        let names = [];
        let cursor = DateTime.now().startOf('week');

        for(let i = 0; i < 7; i++){

            names.push(cursor.toFormat('ccc'));
            cursor = cursor.plus({ days: 1 });
        }

        return names;
    }

    get selectedDateLuxon(): DateTime {

        return this.selectedDate && !this.period ? DateTime.fromISO(this.selectedDate) : null;
    }

    get selectedPeriodLuxon(): DateTime[] {

        return this.selectedPeriod && this.period ? this.selectedPeriod.map(date => DateTime.fromISO(date)) : null;
    }

    get selectedPeriodInterval(): Interval {

        return this.selectedPeriodLuxon?.length === 2 ? Interval.fromDateTimes(this.selectedPeriodLuxon[0], this.selectedPeriodLuxon[1]) : null;
    }

    get visibleDateLuxon() {

        return this.visibleDate ? DateTime.fromISO(this.visibleDate) : null;
    }

    get timeParts() {

        if(!this.timeValue){
            return null;
        }

        const parts = this.timeValue.split(':');

        return {
            hour: parseInt(parts[0]),
            minute: parseInt(parts[1])
        };
    }

    get timeEnabled(){

        return this.time && !this.period;
    }
}

export type DatePickerComp = InstanceType<typeof DatePicker>
export type DatePickerProps = ComponentProps<DatePickerComp>
export const datePickerCompMixin = createComponentMixin(DatePicker)

</script>
<style src="../../../scss/component/control/DatePicker.scss" lang="scss"></style>